import { Injectable } from '@angular/core';
import { ResponseUserDto } from '@common/generated-api/models/response-user-dto';
import { UserCreateForm } from '@common/generated-api/models/user-create-form';
import { UserUpdateForm } from '@common/generated-api/models/user-update-form';
import { AdminUsersApiService } from '@common/generated-api/services/admin-users-api.service';
// eslint-disable-next-line max-len
import {
  ISearchResultItemGeneric
} from '@shared/components/search/multiple-select-with-search/multiple-select-with-search.component';
import { AppService } from '@shared/services/app.service';
import { CurrentUserService, ICurrentCompanyType } from '@shared/services/current-user.service';
import { UserRoles } from '@shared/types/api-types';
import { Observable, throwError } from 'rxjs';
import { catchError, finalize, map, switchMap } from 'rxjs/operators';

@Injectable({
  providedIn: 'root',
})
export class UsersService {
  constructor(
    private appService: AppService,
    private currentUserService: CurrentUserService,
    private adminUsersApiService: AdminUsersApiService
  ) {
  }

  getUser(userId: number) {
    const stop = this.appService.startLoader();
    return this.adminUsersApiService.getUser({ userId }).pipe(finalize(() => stop()));
  }

  searchUsersOfCurrentCompany(
    searchText: string,
    role: UserRoles,
    currentCompany?: ICurrentCompanyType
  ): Observable<ISearchResultItemGeneric<number>[]> {
    const curCompany = currentCompany || this.currentUserService.currentCompanyType$.getValue();
    return this.adminUsersApiService.searchLocalUsers({
      body: {
        ...curCompany,
        offset: 0,
        limit: 20,
        searchText: searchText,
        roles: [ role ]
      }
    }).pipe(
      map(data => data.result.data),
      map(data => data.map(item => ({ id: item.id, name: item.name }))),
    )
  }

  createUser(body: UserCreateForm): Observable<ResponseUserDto> {
    const stop = this.appService.startLoader();
    return this.adminUsersApiService
      .createUser({ body }, {
        disableErrorCatching: true,
      })
      .pipe(finalize(() => stop()));
  }

  updateUser(userId: number, body: UserUpdateForm): Observable<ResponseUserDto> {
    const stop = this.appService.startLoader();
    return this.adminUsersApiService.updateUser({ userId, body }).pipe(finalize(() => stop()));
  }

  removeUser(userId: number) {
    const stop = this.appService.startLoader();
    return this.adminUsersApiService.deleteUser({ userId }).pipe(finalize(() => stop()));
  }

  changeResponsibleAdminToCommon(userId: number): Observable<ResponseUserDto> {
    return this.changeUserRole(userId, UserRoles.ADMIN_RESPONSIBLE, UserRoles.ADMIN_COMMON);
  }

  changeCommonAdminToResponsible(userId: number): Observable<ResponseUserDto> {
    return this.changeUserRole(userId, UserRoles.ADMIN_COMMON, UserRoles.ADMIN_RESPONSIBLE);
  }

  private changeUserRole(userId: number, roleFrom: UserRoles, roleTo: UserRoles): Observable<ResponseUserDto> {
    const stop = this.appService.startLoader();
    return this.adminUsersApiService.getUser({ userId }).pipe(
      finalize(() => stop()),
      catchError((err) => {
        return throwError(err);
      }),
      switchMap((user) => {
        if (user.result.role !== roleFrom) {
          return throwError('Current user role must be ' + roleFrom);
        } else {
          return this.updateUser(userId, {
            email: user.result.email || undefined,
            name: user.result.name,
            position: user.result.position,
            phone: user.result.phone,
            phoneCountry: user.result.phoneCountry,
            picture: user.result.picture?.id,
            role: roleTo,
          });
        }
      }),
    );
  }
}
